import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Input } from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';
import { Badge } from '../../blog/components/ui';
import { useToast } from '../../../shared/components/toast';
import { contentPagesApi } from '../api/contentPagesApi';
import { useContentPage, useCreateContentPage, useUpdateContentPage } from '../hooks/useContentPages';
import { RichTextEditor } from '../components/RichTextEditor';
import {
  CONTENT_PAGE_STATUS_LABELS,
  SLUG_SUGGESTIONS,
  buildCreatePayload,
  buildUpdatePayload,
  derivePageStatus,
  generateSlug,
} from '../payload';

const emptyBox =
  'text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]';

/** Thrown-error shape from `shared/utils/fetch.ts`'s `throwResponseError`. */
interface ApiError {
  status?: number;
  message?: string;
}

/**
 * Create/edit page for a single content page (PE-2175).
 *
 * Reached at `/content-pages/new` (create) and `/content-pages/:id` (edit) — the
 * two routes share this component; `id` is `undefined` only on the literal `/new`
 * route (react-router ranks that static segment above the `:id` param, so "new"
 * never gets swallowed as an id).
 */
export function ContentPageEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isCreate = id === undefined;
  const navigate = useNavigate();
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CONTENT_PAGE_MANAGE);
  const { toast } = useToast();

  const queryClient = useQueryClient();
  const pageQuery = useContentPage(selectedTenantId, isCreate ? undefined : id);
  const createMutation = useCreateContentPage(selectedTenantId);
  const updateMutation = useUpdateContentPage(selectedTenantId, id ?? '');
  const [isPublishing, setIsPublishing] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [body, setBody] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);

  // What the form looked like the moment it was last saved (or loaded) — the
  // baseline the unsaved-changes guard diffs against.
  const savedSnapshot = useRef({ title: '', slug: '', body: '' });

  // Load an existing page into the form exactly once per page id, so re-fetches
  // (e.g. after a mutation invalidates the query) don't clobber in-progress edits.
  const loadedForId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isCreate || !pageQuery.data) return;
    if (loadedForId.current === pageQuery.data.id) return;
    loadedForId.current = pageQuery.data.id;
    setTitle(pageQuery.data.title);
    setSlug(pageQuery.data.slug);
    setBody(pageQuery.data.draftBody);
    savedSnapshot.current = { title: pageQuery.data.title, slug: pageQuery.data.slug, body: pageQuery.data.draftBody };
  }, [isCreate, pageQuery.data]);

  // Slug auto-fills from the title on create, until the merchant edits it by hand.
  useEffect(() => {
    if (!isCreate || slugTouched) return;
    setSlug(generateSlug(title));
  }, [isCreate, slugTouched, title]);

  const isDirty =
    title !== savedSnapshot.current.title || slug !== savedSnapshot.current.slug || body !== savedSnapshot.current.body;

  // Unsaved-changes guard for a tab close/refresh. In-app back-navigation is
  // covered by the confirm in `goBack` below — this app uses declarative
  // `<Routes>`, which has no `useBlocker` to intercept router navigation itself.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const goBack = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    navigate('/content-pages');
  };

  const status = pageQuery.data ? derivePageStatus(pageQuery.data) : null;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSaveDraft = async () => {
    setSlugError(null);
    try {
      if (isCreate) {
        const created = await createMutation.mutateAsync(buildCreatePayload({ title, slug, body }));
        savedSnapshot.current = { title: created.title, slug: created.slug, body: created.draftBody };
        toast.success('Draft saved');
        navigate(`/content-pages/${created.id}`, { replace: true });
        return created;
      }
      const updated = await updateMutation.mutateAsync(buildUpdatePayload({ title, body }));
      savedSnapshot.current = { title: updated.title, slug: updated.slug, body: updated.draftBody };
      toast.success('Draft saved');
      return updated;
    } catch (e) {
      const err = e as ApiError;
      // ApiFetch already toasted this (non-401 failures always are) — inline the
      // 409 on the slug field instead of a second toast.
      if (err.status === 409) {
        setSlugError('A page with this slug already exists');
      }
      return undefined;
    }
  };

  // Publish always calls the api directly (not a route-id-bound mutation hook):
  // on create, `saved.id` is the page that was JUST created this call, which is
  // not yet reflected in the `:id` route param at the point this runs.
  const handlePublish = async () => {
    const saved = await handleSaveDraft();
    if (!saved) return;
    setIsPublishing(true);
    try {
      await contentPagesApi.publish(saved.id);
      queryClient.invalidateQueries({ queryKey: ['content-pages', selectedTenantId] });
      queryClient.invalidateQueries({ queryKey: ['content-page', selectedTenantId, saved.id] });
      toast.success('Page published');
    } catch {
      // ApiFetch already toasted the failure.
    } finally {
      setIsPublishing(false);
    }
  };

  if (!selectedTenantId) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        <div className={emptyBox}>Select a merchant to manage their pages.</div>
      </div>
    );
  }

  if (!isCreate && pageQuery.isLoading) {
    return <div className="py-16 text-center text-muted-foreground">Loading page…</div>;
  }

  if (!isCreate && pageQuery.error) {
    return <div className="py-16 text-center text-red-500">Failed to load page.</div>;
  }

  return (
    <div className="py-6 max-w-4xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="text-[14px] text-muted-foreground hover:underline">
            ← Back to Pages & policies
          </button>
          {status && <Badge variant={status === 'DRAFT' ? 'outline' : status === 'PUBLISHED_EDITED' ? 'secondary' : 'default'}>{CONTENT_PAGE_STATUS_LABELS[status]}</Badge>}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={isSaving || isPublishing} loading={isSaving} onClick={handleSaveDraft}>
              Save draft
            </Button>
            <Button size="sm" disabled={isSaving || isPublishing} loading={isPublishing} onClick={handlePublish}>
              Publish
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={!canManage}
          placeholder="e.g. Shipping Policy"
        />

        <div>
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
              setSlugError(null);
            }}
            disabled={!isCreate || !canManage}
            placeholder="e.g. shipping-policy"
          />
          {slugError && <p className="text-[13px] text-red-500 mt-1">{slugError}</p>}
          {!isCreate && (
            <p className="text-[13px] text-muted-foreground mt-1">
              Slug can’t be changed after a page is created.
              {status && status !== 'DRAFT' && (
                <>
                  {' '}
                  View on store: <code className="text-foreground">/pages/{slug}</code>
                </>
              )}
            </p>
          )}
          {isCreate && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {SLUG_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setSlugTouched(true);
                    setSlug(s);
                  }}
                  className="text-[12px] px-2 py-1 rounded-full bg-muted text-muted-foreground hover:bg-muted/70"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-[14px] font-[600] text-foreground mb-1">Body</label>
          <RichTextEditor content={body} onChange={setBody} placeholder="Write the page content…" />
        </div>
      </div>
    </div>
  );
}
